import { useMutation, useSuspenseQuery } from "@apollo/client";
import { capitaliseFirstLetter } from "@shared/translations/src";
import { useState } from "react";
import { type z } from "zod";
import { DocumentTile } from "@/components/DocumentTile";
import { type documentUploadFormSchema } from "@/components/DocumentUploadForm";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ContactDocumentUploadForm } from "@/contacts/ContactDocumentUploadForm";
import { graphql } from "@/graphql";
import { useToast } from "@/hooks/use-toast";
import { removeNothings } from "@/lib/utils/arrays";
import { extractErrorMessages, getErrorMessage } from "@/lib/utils/errors";

export function ContactDocuments({ contactId }: { contactId: string }) {
  const { data } = useSuspenseQuery(supplyChainPartnerDocumentsQuery, {
    variables: { partner: contactId, status: ["ACTIVE"] },
  });
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { toast } = useToast();
  const [uploadSupplyChainPartnerDocument] = useMutation(uploadSupplyChainPartnerDocumentMutation);
  const [archiveDocument] = useMutation(archiveSupplyChainPartnersDocument);

  const handleDelete = async (documentId: string) => {
    try {
      const result = await archiveDocument({
        variables: {
          input: {
            documents: [documentId],
          },
        },
        refetchQueries: [supplyChainPartnerDocumentsQuery],
      });

      const couldNotArchiveErrors = result.data?.archiveSupplyChainPartnerDocuments?.couldNotArchive;

      if (couldNotArchiveErrors && couldNotArchiveErrors[0]?.reason) {
        toast({
          title: `Error archiving document`,
          description: couldNotArchiveErrors[0]?.reason
            ? capitaliseFirstLetter(couldNotArchiveErrors[0]?.reason)
            : "Unknown error",
          variant: "destructive",
        });

        return;
      }

      const errors = result.errors || [];

      if (errors.length > 0) {
        toast({
          title: `Error deleting document`,
          description: extractErrorMessages(errors),
          variant: "destructive",
        });

        return;
      }

      toast({
        title: "Success",
        description: `Document deleted successfully`,
        variant: "default",
      });
    } catch (error) {
      toast({
        title: "Error deleting document",
        description: getErrorMessage(error),
        variant: "destructive",
      });
    }
  };

  const handleSubmit = async (values: z.infer<typeof documentUploadFormSchema>) => {
    try {
      if (!contactId) {
        toast({
          title: "Error uploading document",
          description: "Contact ID is missing",
          variant: "destructive",
        });

        return;
      }

      const result = await uploadSupplyChainPartnerDocument({
        variables: {
          input: {
            file: values.file,
            name: values.name,
            type: values.type,
            partner: contactId,
          },
        },
        refetchQueries: [supplyChainPartnerDocumentsQuery],
      });

      const errors = result.errors || [];

      if (errors.length > 0) {
        toast({
          title: `Error uploading document.`,
          description: extractErrorMessages(errors),
          variant: "destructive",
        });

        return;
      }

      setIsDialogOpen(false);

      toast({
        title: "Success",
        description: `Document ${values.name} uploaded successfully`,
        variant: "default",
      });
    } catch (error) {
      toast({
        title: "Error uploading document",
        description: getErrorMessage(error),
        variant: "destructive",
      });
    }
  };

  const documents = removeNothings((data?.supplyChainPartnerDocuments?.edges || []).map((edge) => edge?.node));

  return (
    <>
      <DocumentTile
        documents={documents}
        title={`Documents (${documents.length})`}
        onDelete={handleDelete}
        onUpload={() => {
          setIsDialogOpen(true);
        }}
      />

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Upload Document</DialogTitle>
            <DialogDescription className="sr-only">
              Upload your document by dragging and dropping or selecting a file.
            </DialogDescription>
          </DialogHeader>

          <ContactDocumentUploadForm onSubmit={handleSubmit} onCancel={() => setIsDialogOpen(false)} />
        </DialogContent>
      </Dialog>
    </>
  );
}

export const uploadSupplyChainPartnerDocumentMutation = graphql(`
  mutation UploadSupplyChainPartnerDocument($input: UploadSupplyChainPartnerDocumentInput!) {
    uploadSupplyChainPartnerDocument(input: $input) {
      document {
        id
        name
        type {
          id
          label
        }
      }
    }
  }
`);

export const archiveSupplyChainPartnersDocument = graphql(`
  mutation ArchiveSupplyChainPartnerDocument($input: ArchiveSupplyChainPartnerDocumentInput!) {
    archiveSupplyChainPartnerDocuments(input: $input) {
      couldNotArchive {
        id
        reason
      }
      documents {
        id
      }
    }
  }
`);

export const supplyChainPartnerDocumentsQuery = graphql(`
  query SupplyChainPartnerDocuments($partner: ID!, $status: [SupplyChainPartnerDocumentStatusEnumType!]) {
    supplyChainPartnerDocuments(partner: $partner, status: $status) {
      numSupplyChainPartnerDocuments
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      edges {
        node {
          id
          name
          url
          type {
            id
            label
          }
        }
      }
    }
  }
`);
